/**
 * Development Progress Data
 * Mapeamento completo do desenvolvimento do software de automação WhatsApp
 * Atualizar este arquivo conforme o progresso avança
 */

export type TaskStatus = 'completed' | 'in-progress' | 'pending' | 'blocked';

export interface Task {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  progress: number; // 0-100
  subtasks?: Task[];
  nextSteps?: string[];
  estimatedDays?: number;
  completedDate?: string;
}

export interface Phase {
  id: string;
  name: string;
  description: string;
  order: number;
  status: TaskStatus;
  progress: number; // 0-100
  tasks: Task[];
  startDate?: string;
  endDate?: string;
}

export const developmentPhases: Phase[] = [
  {
    id: 'phase-1',
    name: 'Configuração da Meta e Ambiente',
    description: 'Preparar a integração com a API oficial do WhatsApp da Meta',
    order: 1,
    status: 'pending',
    progress: 0,
    startDate: undefined,
    endDate: undefined,
    tasks: [
      {
        id: 'task-1-1',
        title: 'Criar Conta de Desenvolvedor Meta',
        description: 'Registrar e configurar conta de desenvolvedor no Meta for Developers',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Acessar https://developers.facebook.com',
          'Criar conta de desenvolvedor',
          'Verificar email',
        ],
      },
      {
        id: 'task-1-2',
        title: 'Criar App Meta',
        description: 'Criar novo app para a integração WhatsApp',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Ir para "Meus Apps"',
          'Clicar em "Criar App"',
          'Selecionar tipo: "Negócios"',
          'Configurar nome e email',
        ],
      },
      {
        id: 'task-1-3',
        title: 'Obter Token de Acesso Permanente',
        description: 'Gerar token de acesso para chamadas à API do WhatsApp',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Ir para "Configurações" > "Básico"',
          'Copiar App ID e App Secret',
          'Gerar token de acesso permanente',
          'Armazenar com segurança',
        ],
      },
      {
        id: 'task-1-4',
        title: 'Conectar Número WhatsApp Business',
        description: 'Vincular número de telefone à conta WhatsApp Business',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Acessar WhatsApp Manager',
          'Adicionar número de telefone',
          'Verificar número via SMS/chamada',
          'Confirmar vinculação',
        ],
      },
    ],
  },
  {
    id: 'phase-2',
    name: 'Estrutura do Banco de Dados',
    description: 'Criar e configurar o banco de dados para contatos, templates e campanhas',
    order: 2,
    status: 'pending',
    progress: 0,
    startDate: undefined,
    endDate: undefined,
    tasks: [
      {
        id: 'task-2-1',
        title: 'Escolher Provedor de Banco de Dados',
        description: 'Decidir entre MySQL, PostgreSQL ou outro SGBD',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Avaliar opções: MySQL, PostgreSQL, MongoDB',
          'Considerar custo e escalabilidade',
          'Definir escolha final',
        ],
      },
      {
        id: 'task-2-2',
        title: 'Criar Tabelas de Contatos',
        description: 'Estruturar tabela de contatos com campos customizados',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar tabela "contacts"',
          'Adicionar campos: phone_number, name, email, company_name, last_interaction_at',
          'Criar índices para performance',
          'Testar inserção de dados',
        ],
      },
      {
        id: 'task-2-3',
        title: 'Criar Tabelas de Templates',
        description: 'Estruturar tabelas para armazenar templates de mensagens',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar tabela "templates"',
          'Adicionar campos: name, header, body, buttons, status',
          'Criar tabela "template_components" para componentes complexos',
          'Testar estrutura',
        ],
      },
      {
        id: 'task-2-4',
        title: 'Criar Tabelas de Campanhas',
        description: 'Estruturar tabelas para campanhas de disparo em massa',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar tabela "campaigns"',
          'Adicionar campos: name, template_id, target_tags, status, sent_at',
          'Criar tabela "campaign_metrics" para métricas',
          'Testar estrutura',
        ],
      },
      {
        id: 'task-2-5',
        title: 'Criar Tabelas de Automações',
        description: 'Estruturar tabelas para fluxos de automação',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar tabela "automations"',
          'Adicionar campos: name, trigger, action, template_id, status',
          'Criar tabela "automation_logs" para rastreamento',
          'Testar estrutura',
        ],
      },
    ],
  },
  {
    id: 'phase-3',
    name: 'Backend API',
    description: 'Desenvolver endpoints da API para gerenciar contatos, templates e campanhas',
    order: 3,
    status: 'pending',
    progress: 0,
    startDate: undefined,
    endDate: undefined,
    tasks: [
      {
        id: 'task-3-1',
        title: 'Configurar Servidor Express',
        description: 'Inicializar servidor Node.js com Express',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar projeto Node.js',
          'Instalar Express, dotenv, cors',
          'Configurar variáveis de ambiente',
          'Testar servidor rodando',
        ],
      },
      {
        id: 'task-3-2',
        title: 'Endpoints de Contatos',
        description: 'Criar CRUD para gerenciar contatos',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'POST /api/contacts - criar contato',
          'GET /api/contacts - listar contatos',
          'GET /api/contacts/:id - obter contato',
          'PUT /api/contacts/:id - atualizar contato',
          'DELETE /api/contacts/:id - deletar contato',
        ],
      },
      {
        id: 'task-3-3',
        title: 'Endpoints de Templates',
        description: 'Criar CRUD para gerenciar templates WhatsApp',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'POST /api/templates - criar template',
          'GET /api/templates - listar templates',
          'PUT /api/templates/:id - atualizar template',
          'DELETE /api/templates/:id - deletar template',
          'POST /api/templates/:id/sync - sincronizar com Meta',
        ],
      },
      {
        id: 'task-3-4',
        title: 'Endpoints de Campanhas',
        description: 'Criar endpoints para disparar campanhas em massa',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'POST /api/campaigns - criar campanha',
          'GET /api/campaigns - listar campanhas',
          'POST /api/campaigns/:id/send - disparar campanha',
          'GET /api/campaigns/:id/metrics - obter métricas',
        ],
      },
      {
        id: 'task-3-5',
        title: 'Integração com API WhatsApp',
        description: 'Implementar chamadas à API oficial do WhatsApp',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Instalar axios ou fetch',
          'Criar função para enviar mensagens',
          'Implementar tratamento de erros',
          'Testar envio de mensagens',
        ],
      },
    ],
  },
  {
    id: 'phase-4',
    name: 'Frontend Admin Dashboard',
    description: 'Desenvolver interface de administração para gerenciar tudo',
    order: 4,
    status: 'in-progress',
    progress: 15,
    startDate: new Date().toISOString().split('T')[0],
    endDate: undefined,
    tasks: [
      {
        id: 'task-4-1',
        title: 'Dashboard de Progresso',
        description: 'Criar página com barra de progresso e próximos passos (ATUAL)',
        status: 'in-progress',
        progress: 50,
        nextSteps: [
          'Estruturar layout com sidebar',
          'Criar componentes de fase e tarefa',
          'Adicionar barras de progresso',
          'Implementar lógica de atualização',
        ],
      },
      {
        id: 'task-4-2',
        title: 'Gerenciador de Contatos',
        description: 'Interface para importar, visualizar e editar contatos',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar tabela de contatos',
          'Adicionar filtros e busca',
          'Implementar importação via CSV',
          'Adicionar edição inline',
        ],
      },
      {
        id: 'task-4-3',
        title: 'Editor de Templates',
        description: 'Interface visual para criar e editar templates',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar formulário de template',
          'Adicionar preview de mensagem',
          'Implementar editor de componentes',
          'Testar sincronização com Meta',
        ],
      },
      {
        id: 'task-4-4',
        title: 'Gerenciador de Campanhas',
        description: 'Interface para criar e disparar campanhas',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar formulário de campanha',
          'Adicionar seletor de público',
          'Implementar agendamento',
          'Mostrar métricas em tempo real',
        ],
      },
      {
        id: 'task-4-5',
        title: 'Dashboard de Métricas',
        description: 'Visualizar performance de campanhas e engajamento',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar gráficos de envios/entregas',
          'Adicionar métricas por tag/órgão',
          'Implementar filtros de data',
          'Mostrar taxa de engajamento',
        ],
      },
    ],
  },
  {
    id: 'phase-5',
    name: 'Automações e Fluxos',
    description: 'Implementar sistema de automações baseado em gatilhos',
    order: 5,
    status: 'pending',
    progress: 0,
    startDate: undefined,
    endDate: undefined,
    tasks: [
      {
        id: 'task-5-1',
        title: 'Engine de Automações',
        description: 'Criar sistema para processar gatilhos e executar ações',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Definir tipos de gatilhos',
          'Implementar processador de eventos',
          'Criar fila de processamento',
          'Testar fluxos básicos',
        ],
      },
      {
        id: 'task-5-2',
        title: 'Gatilhos de Contato',
        description: 'Implementar gatilhos: novo contato, tag adicionada, etc',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Gatilho: novo contato criado',
          'Gatilho: tag adicionada',
          'Gatilho: última interação',
          'Testar cada gatilho',
        ],
      },
      {
        id: 'task-5-3',
        title: 'Ações de Automação',
        description: 'Implementar ações: enviar mensagem, adicionar tag, etc',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Ação: enviar template',
          'Ação: adicionar/remover tag',
          'Ação: atualizar campo customizado',
          'Testar cada ação',
        ],
      },
      {
        id: 'task-5-4',
        title: 'Interface de Automações',
        description: 'Criar UI para criar e gerenciar automações',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar editor visual de fluxos',
          'Adicionar seletor de gatilhos',
          'Implementar seletor de ações',
          'Testar criação de automações',
        ],
      },
    ],
  },
  {
    id: 'phase-6',
    name: 'Testes e Otimizações',
    description: 'Testar, debugar e otimizar toda a plataforma',
    order: 6,
    status: 'pending',
    progress: 0,
    startDate: undefined,
    endDate: undefined,
    tasks: [
      {
        id: 'task-6-1',
        title: 'Testes Unitários',
        description: 'Escrever testes para funções críticas',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Configurar Jest/Vitest',
          'Testar funções de API',
          'Testar lógica de automações',
          'Atingir 80% de cobertura',
        ],
      },
      {
        id: 'task-6-2',
        title: 'Testes de Integração',
        description: 'Testar fluxos completos ponta a ponta',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Testar criação de contato até envio',
          'Testar disparo de campanha',
          'Testar automações',
          'Testar sincronização com Meta',
        ],
      },
      {
        id: 'task-6-3',
        title: 'Otimizações de Performance',
        description: 'Otimizar queries, cache e carregamento',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Analisar queries lentas',
          'Adicionar índices no BD',
          'Implementar cache',
          'Otimizar frontend',
        ],
      },
      {
        id: 'task-6-4',
        title: 'Segurança',
        description: 'Implementar autenticação, validação e proteção',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Adicionar autenticação JWT',
          'Validar inputs',
          'Proteger chaves de API',
          'Implementar rate limiting',
        ],
      },
    ],
  },
  {
    id: 'phase-7',
    name: 'Deploy e Documentação',
    description: 'Preparar para produção e documentar tudo',
    order: 7,
    status: 'pending',
    progress: 0,
    startDate: undefined,
    endDate: undefined,
    tasks: [
      {
        id: 'task-7-1',
        title: 'Documentação da API',
        description: 'Criar documentação completa dos endpoints',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Usar Swagger/OpenAPI',
          'Documentar cada endpoint',
          'Adicionar exemplos de uso',
          'Publicar documentação',
        ],
      },
      {
        id: 'task-7-2',
        title: 'Documentação do Usuário',
        description: 'Criar guias de uso da plataforma',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Criar guia de primeiros passos',
          'Documentar cada feature',
          'Adicionar screenshots',
          'Criar FAQ',
        ],
      },
      {
        id: 'task-7-3',
        title: 'Deploy em Produção',
        description: 'Fazer deploy da aplicação',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Escolher provedor (AWS, Heroku, etc)',
          'Configurar variáveis de ambiente',
          'Fazer deploy',
          'Monitorar aplicação',
        ],
      },
      {
        id: 'task-7-4',
        title: 'Monitoramento e Suporte',
        description: 'Configurar logs, alertas e suporte',
        status: 'pending',
        progress: 0,
        nextSteps: [
          'Configurar logging centralizado',
          'Adicionar alertas',
          'Criar sistema de suporte',
          'Monitorar performance',
        ],
      },
    ],
  },
];

/**
 * Calcular progresso geral do projeto
 */
export function calculateOverallProgress(): number {
  const totalTasks = developmentPhases.reduce(
    (acc, phase) => acc + phase.tasks.length,
    0
  );
  const completedTasks = developmentPhases.reduce(
    (acc, phase) =>
      acc +
      phase.tasks.filter((task) => task.status === 'completed').length,
    0
  );
  return Math.round((completedTasks / totalTasks) * 100);
}

/**
 * Obter próximas tarefas prioritárias
 */
export function getNextPrioritizedTasks(limit: number = 5): Task[] {
  const allTasks: Task[] = [];
  
  developmentPhases.forEach((phase) => {
    phase.tasks.forEach((task) => {
      allTasks.push(task);
    });
  });

  // Ordenar por: pending > in-progress, depois por ordem de fase
  return allTasks
    .filter((task) => task.status !== 'completed')
    .sort((a, b) => {
      const statusOrder: Record<TaskStatus, number> = { 'in-progress': 0, pending: 1, blocked: 2, completed: 3 };
      return (
        (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4)
      );
    })
    .slice(0, limit);
}
